// packages/api/src/hooks/useConsultantApproval.ts

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import type {
  ApprovalQueueItem,
  ApprovalQueueFilters,
  RegistrationDetail,
  ApprovalStats,
  ApproveRegistrationPayload,
  RejectRegistrationPayload,
  RequestCorrectionPayload,
  AddAdminNotePayload,
  RegistrationActionResponse,
  ConferRegistrationBlockPayload,
  ConferRegistrationBlockResponse,
  SaveRegistrationBlockFieldsPayload,
  SaveRegistrationBlockFieldsResponse,
  RegistrationAnalysisHistoryFilters,
  RegistrationAnalysisHistoryItem,
  PaginatedResponse,
} from '@alamo/types';
import { extractApiError } from '@alamo/utils';

import { apiClient } from '../client';
import { queryKeys } from '../query-keys';

import { createQueueListingHook } from './queue/createQueueListingHook';

/**
 * Response shape for POST /consultant-approval/:id/start-review (Plan 02 Task 2).
 * Backend returns: { id, registrationStatus, reviewStartedAt, reviewStartedBy }.
 */
export interface StartReviewResponse {
  id: string;
  registrationStatus: string;
  reviewStartedAt: string;
  reviewStartedBy: string;
}

/**
 * Paginated approval queue — an instance of the shared queue listing factory,
 * so it always yields a normalized PaginatedResponse<ApprovalQueueItem>.
 * GET /api/v1/consultant-approval/queue — requires admin_registrations:read.
 */
export const useApprovalQueue = createQueueListingHook<ApprovalQueueItem, ApprovalQueueFilters>({
  baseQueryKey: queryKeys.consultantApproval.all,
  queryKey: (filters) => queryKeys.consultantApproval.queue(filters),
  fetchPage: async (filters) => {
    // ResponseInterceptor wraps every backend payload in { success, data,
    // timestamp } and the service returns { data: rows, total: N }, so axios
    // lands on response.data.data = { data, total } (the factory normalizes it).
    const response = await apiClient.get<{
      data: { data: ApprovalQueueItem[]; total: number };
    }>('/consultant-approval/queue', { params: filters });
    return response.data.data;
  },
  getPageParams: (filters) => ({ page: filters?.page, limit: filters?.limit }),
});

export function useRegistrationAnalysisHistory(filters: RegistrationAnalysisHistoryFilters) {
  return useQuery({
    queryKey: queryKeys.consultantApproval.history(filters),
    queryFn: async (): Promise<PaginatedResponse<RegistrationAnalysisHistoryItem>> => {
      const response = await apiClient.get<{
        data: { count: number; rows: RegistrationAnalysisHistoryItem[] };
      }>('/consultant-approval/history', { params: filters });
      const { count, rows } = response.data.data;
      const page = filters.page ?? 1;
      const limit = filters.limit ?? 20;
      return {
        items: rows,
        total: count,
        page,
        limit,
        totalPages: Math.max(1, Math.ceil(count / limit)),
      };
    },
  });
}

/**
 * Fetch full registration detail with document presigned URLs.
 * GET /api/v1/consultant-approval/:id
 * Requires admin_registrations:read permission.
 */
export function useRegistrationDetail(id: string) {
  return useQuery({
    queryKey: queryKeys.consultantApproval.detail(id),
    queryFn: async () => {
      const response = await apiClient.get<{ data: RegistrationDetail }>(
        `/consultant-approval/${id}`,
      );
      return response.data.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Approve a registration.
 * POST /api/v1/consultant-approval/:id/approve
 * Requires admin_registrations:update permission.
 */
export function useApproveRegistration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: ApproveRegistrationPayload & { id: string }) => {
      const response = await apiClient.post<{ data: RegistrationActionResponse }>(
        `/consultant-approval/${id}/approve`,
        data,
      );
      return response.data.data;
    },
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.consultantApproval.all });
      void qc.invalidateQueries({ queryKey: queryKeys.consultantApproval.detail(id) });
    },
  });
}

/**
 * Approve or reject one registration block (identity/company/address/
 * contact), optionally editing its whitelisted fields on approve.
 * POST /api/v1/consultant-approval/:id/conference/:blockKey
 * Requires admin_registrations:update permission.
 */
export function useConferRegistrationBlock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      blockKey,
      ...data
    }: ConferRegistrationBlockPayload): Promise<ConferRegistrationBlockResponse> => {
      const response = await apiClient.post<{ data: ConferRegistrationBlockResponse }>(
        `/consultant-approval/${id}/conference/${blockKey}`,
        data,
      );
      return response.data.data;
    },
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.consultantApproval.detail(id) });
    },
  });
}

/**
 * Saves whitelisted field edits for one registration block WITHOUT deciding
 * approve/reject — independent from useConferRegistrationBlock above
 * (Pedro 2026-07-03: editing+saving and approving/rejecting are two separate
 * actions in the UI, each with its own button).
 * PATCH /api/v1/consultant-approval/:id/conference/:blockKey/fields
 * Requires admin_registrations:update permission.
 */
export function useSaveRegistrationBlockFields() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      blockKey,
      updatedFields,
    }: SaveRegistrationBlockFieldsPayload): Promise<SaveRegistrationBlockFieldsResponse> => {
      const response = await apiClient.patch<{ data: SaveRegistrationBlockFieldsResponse }>(
        `/consultant-approval/${id}/conference/${blockKey}/fields`,
        { updatedFields },
      );
      return response.data.data;
    },
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.consultantApproval.detail(id) });
    },
  });
}

/**
 * Reject a registration.
 * POST /api/v1/consultant-approval/:id/reject
 * Requires admin_registrations:update permission.
 */
export function useRejectRegistration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: RejectRegistrationPayload & { id: string }) => {
      const response = await apiClient.post<{ data: RegistrationActionResponse }>(
        `/consultant-approval/${id}/reject`,
        data,
      );
      return response.data.data;
    },
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.consultantApproval.all });
      void qc.invalidateQueries({ queryKey: queryKeys.consultantApproval.detail(id) });
    },
  });
}

/**
 * Request correction on a registration.
 * POST /api/v1/consultant-approval/:id/correction
 * Requires admin_registrations:update permission.
 */
export function useRequestCorrection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: RequestCorrectionPayload & { id: string }) => {
      const response = await apiClient.post<{ data: RegistrationActionResponse }>(
        `/consultant-approval/${id}/correction`,
        data,
      );
      return response.data.data;
    },
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.consultantApproval.all });
      void qc.invalidateQueries({ queryKey: queryKeys.consultantApproval.detail(id) });
    },
  });
}

/**
 * Revert a rejection (rejected → under_review).
 * POST /api/v1/consultant-approval/:id/reopen
 * Requires admin_registrations:revert permission.
 */
export function useReopenRegistration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post<{ data: RegistrationActionResponse }>(
        `/consultant-approval/${id}/reopen`,
        {},
      );
      return response.data.data;
    },
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: queryKeys.consultantApproval.all });
      void qc.invalidateQueries({ queryKey: queryKeys.consultantApproval.detail(id) });
    },
  });
}

/**
 * Add admin note to a registration.
 * POST /api/v1/consultant-approval/:id/notes
 * Requires admin_registrations:update permission.
 */
export function useAddAdminNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: AddAdminNotePayload & { id: string }) => {
      const response = await apiClient.post<{ data: { id: string; content: string } }>(
        `/consultant-approval/${id}/notes`,
        data,
      );
      return response.data.data;
    },
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.consultantApproval.detail(id) });
    },
  });
}

/**
 * Fetch approval queue statistics (counts by status).
 * GET /api/v1/consultant-approval/stats
 * Requires admin_registrations:read permission.
 */
export function useApprovalStats(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.consultantApproval.stats(),
    queryFn: async () => {
      const response = await apiClient.get<{ data: ApprovalStats }>('/consultant-approval/stats');
      return response.data.data;
    },
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Transition a pending registration to under_review (D-06).
 * POST /api/v1/consultant-approval/:id/start-review
 * Requires admin_registrations:update permission.
 *
 * On 409 REGISTRATION_INVALID_STATUS (status !== 'pending'): refetches detail so
 * the UI reflects the actual backend state (e.g., another admin started first).
 */
export function useStartReview() {
  const qc = useQueryClient();
  const { t } = useTranslation(['admin', 'errors']);
  return useMutation({
    mutationFn: async (id: string): Promise<StartReviewResponse> => {
      const res = await apiClient.post<{ data: StartReviewResponse }>(
        `/consultant-approval/${id}/start-review`,
        {},
      );
      return res.data.data;
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: queryKeys.consultantApproval.detail(data.id) });
      void qc.invalidateQueries({ queryKey: queryKeys.consultantApproval.all });
      toast.success(t('admin:registrations.toast.review_started'));
    },
    onError: (err: unknown, id: string) => {
      const { code, message } = extractApiError(err);
      toast.error(message ?? t('errors:INTERNAL_ERROR', { defaultValue: t('errors:generic') }));
      if (code === 'REGISTRATION_INVALID_STATUS') {
        void qc.invalidateQueries({ queryKey: queryKeys.consultantApproval.detail(id) });
      }
    },
  });
}
